import { Visibility } from "@material-ui/icons";
import {
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import Modal from "components/modal/Modal";
import ModalSubmitFooter from "components/modal/ModalSubmitFooter";
import Select from "components/select/Select";
import { useFormik } from "formik";
import { enqueueSnackbar } from "notistack";
import { useGetListProvince } from "service/query/common.query";
import {
  useCreateExtension,
  useEditExtension,
  useGetExtensionPassword,
  useGetExtensionSecretKey,
} from "service/query/extension.query";
import { useGetListPhoneNumber } from "service/query/phone-number.query";
import {
  getCheckboxFieldProps,
  getSelectFieldProps,
  getTextFieldProps,
} from "ultis/form";
import * as Yup from "yup";

const ExtensionModal = ({ open, onClose, currentItem, mode }) => {
  const readOnly = mode === "view";
  const { data: listPhoneNumber } = useGetListPhoneNumber();
  const { data: listProvince } = useGetListProvince();

  const createExtensionQuery = useCreateExtension();
  const editExtensionQuery = useEditExtension(currentItem?._id);
  const { data: extensionSecretKey } = useGetExtensionSecretKey();

  const {
    data: extensionPassword,
    isFetching: loadingExtensionPassword,
    refetch: fetchingExtensionPassword,
  } = useGetExtensionPassword(currentItem?._id, extensionSecretKey, {
    enabled: false,
  });

  const handleSaveExtension = async (values) => {
    const payload = {
      ...values,
      name: values.name.toString(),
      tags: values.tags
        ? values.tags.split(",").map((item) => item.trim())
        : [],
    };

    if (currentItem) {
      editExtensionQuery.mutate(payload, {
        onSuccess: onClose,
      });
    } else {
      createExtensionQuery.mutate(payload, {
        onSuccess: onClose,
      });
    }
  };

  const formik = useFormik({
    initialValues: {
      name: currentItem?.name || "",
      defaultNumberId: currentItem?.defaultNumberId || "",
      tags: currentItem?.tags?.join(", ") || "",
      type: currentItem?.type || "",
      agentView: currentItem?.agentView || false,
      subOrg: currentItem?.subOrg || "",
    },
    validationSchema: Yup.object().shape({
      name: Yup.string()
        .min(3, "Tên máy lẻ trong khoảng 3-8 ký tự")
        .max(8, "Tên máy lẻ trong khoảng 3-8 ký tự")
        .required("Không được để trống"),
      type: Yup.string().required("Không được để trống"),
    }),
    onSubmit: handleSaveExtension,
  });

  const getModalTitle = () => {
    if (mode === "view") return "Thông tin chi tiết máy lẻ";
    if (mode === "edit") return "Chỉnh sửa thông tin máy lẻ";
    return "Thêm máy lẻ";
  };

  const handleViewExtensionPassword = () => {
    if (extensionSecretKey && currentItem?._id) {
      fetchingExtensionPassword();
      return;
    }

    enqueueSnackbar({
      variant: "error",
      message: "Xin thử lại sau!",
    });
  };

  return (
    <Modal open={open} title={getModalTitle()} onClose={onClose} maxWidth="md">
      <form
        onSubmit={formik.handleSubmit}
        style={{ pointerEvents: readOnly ? "none" : "auto" }}>
        <Stack spacing={2}>
          <TextField
            {...getTextFieldProps(formik, "name", readOnly)}
            label="Tên máy lẻ"
            variant="outlined"
            autoFocus={!readOnly}
            type="number"
            fullWidth
            sx={{ mt: 1 }}
          />

          <Select
            {...getSelectFieldProps(formik, "defaultNumberId", readOnly)}
            label="Đầu số"
            options={[{ label: "Random", value: "random" }].concat(
              listPhoneNumber?.map((item) => ({
                label: item.id,
                value: item.id,
              })) || []
            )}
          />

          <TextField
            {...getTextFieldProps(formik, "tags", readOnly)}
            label="Tags"
            variant="outlined"
            fullWidth
          />

          <TextField
            {...getTextFieldProps(formik, "type", readOnly)}
            select
            label="Loại"
            variant="outlined"
            fullWidth>
            {[
              { label: "sip", value: "sip" },
              { label: "webrtc", value: "webrtc" },
            ].map((item) => (
              <MenuItem value={item.value} key={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>

          <Select
            {...getSelectFieldProps(formik, "subOrg", readOnly)}
            label="Bộ phận"
            options={
              listProvince?.map((item) => ({
                label: item.name,
                value: item.code,
              })) || []
            }
            allowClear
          />

          {(mode === "view" || mode === "edit") && (
            <TextField
              label="Tên tài khoản"
              variant="outlined"
              value={currentItem?.username || ""}
              InputProps={{
                readOnly: true,
              }}
              fullWidth
            />
          )}

          {mode === "view" && (
            <TextField
              label="Password"
              variant="outlined"
              value={extensionPassword || "******"}
              type={extensionPassword ? "text" : "password"}
              InputProps={{
                readOnly: true,
                endAdornment: !extensionPassword && (
                  <InputAdornment
                    position="end"
                    sx={{
                      pointerEvents: "auto",
                    }}>
                    {loadingExtensionPassword && <CircularProgress size={20} />}
                    {!loadingExtensionPassword && (
                      <Tooltip title="Xem mật khẩu">
                        <IconButton
                          onClick={handleViewExtensionPassword}
                          edge="end">
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    )}
                  </InputAdornment>
                ),
              }}
              fullWidth
            />
          )}

          {mode === "edit" && (
            <TextField
              label="Password"
              variant="outlined"
              {...getTextFieldProps(formik, "password")}
              fullWidth
            />
          )}

          {currentItem && (
            <>
              {currentItem.type !== "sip" && currentItem.pbx?.webrtc && (
                <>
                  <TextField
                    label="Protocol"
                    variant="outlined"
                    value={currentItem.pbx.webrtc.protocol}
                    fullWidth
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                  <TextField
                    label="Host"
                    variant="outlined"
                    value={currentItem.pbx.webrtc.host}
                    fullWidth
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                  <TextField
                    label="Path"
                    variant="outlined"
                    value={currentItem.pbx.webrtc.path}
                    fullWidth
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                  <TextField
                    label="Port"
                    variant="outlined"
                    value={currentItem.pbx.webrtc.port}
                    fullWidth
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </>
              )}

              {currentItem.type === "sip" && (
                <TextField
                  fullWidth
                  label="IP tổng đài"
                  variant="outlined"
                  value={currentItem.pbx?.externalIp}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              )}
            </>
          )}

          <FormControlLabel
            control={
              <Checkbox
                {...getCheckboxFieldProps(formik, "agentView", readOnly)}
              />
            }
            label="Hiển thị trên danh sách chọn"
          />
        </Stack>

        {!readOnly && (
          <ModalSubmitFooter
            onCancel={onClose}
            loading={
              createExtensionQuery.isPending || editExtensionQuery.isPending
            }
          />
        )}
      </form>
    </Modal>
  );
};

export default ExtensionModal;
